import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ShareStrip from "@/components/common/ShareStrip";
import StripePaymentModal from "@/components/payment/StripePaymentModal";
import MediaUploader from "@/components/media/MediaUploader";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Loader2,
  Calendar,
  DollarSign,
  Mic,
  User,
  Mail,
  Phone,
  Link as LinkIcon,
  AlertTriangle,
  Sparkles,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_GROUPS = [
  {
    key: "quick",
    label: "Quick Picks",
    desc: "Free or low-cost — start here",
    defaultOpen: true,
    items: [
      {
        key: "test_run",
        label: "Test Run",
        price: "$150",
        desc: "30-min studio trial · same gear, no risk",
      },
      {
        key: "custom",
        label: "Custom Project",
        price: "Quote",
        desc: "Tell us what you need — we'll scope it",
      },
    ],
  },
  {
    key: "creator",
    label: "Creator Packages",
    desc: "Podcasters & content creators",
    defaultOpen: true,
    items: [
      {
        key: "studio_day",
        label: "Studio Day",
        price: "$605",
        desc: "Up to 2-hr session · self-led · post-production",
      },
      {
        key: "creator_launch",
        label: "Creator Launch",
        price: "$1,045",
        desc: "Episode + edit + 3 short-form clips",
      },
      {
        key: "content_partner",
        label: "Content Partner",
        price: "$1,540/mo",
        desc: "2 sessions/mo · 6 clips · strategy call",
      },
    ],
  },
  {
    key: "brand",
    label: "Brand Packages",
    desc: "Businesses & full-service media",
    defaultOpen: false,
    items: [
      {
        key: "brand_shoot_day",
        label: "Brand Shoot Day",
        price: "$850",
        desc: "Half-day brand shoot · 15–20 photos + 3 clips",
      },
      {
        key: "content_engine",
        label: "Content Engine",
        price: "$1,500/mo",
        desc: "Monthly content shoot · 8 posts · 4 reels",
      },
      {
        key: "social_command",
        label: "Social Command",
        price: "$3,000/mo",
        desc: "Full media management · post + manage + report",
      },
    ],
  },
];

const PACKAGES = PACKAGE_GROUPS.flatMap((g) => g.items);

// Package keys eligible for a card-on-file deposit. Excludes "custom" (needs a
// scoped quote first) and the recurring retainer packages (invoiced monthly).
const DEPOSITABLE_PACKAGES = new Set([
  "test_run",
  "studio_day",
  "creator_launch",
  "brand_shoot_day",
]);

const STEPS = ["Package", "Schedule", "Project", "Contact", "Done"];

const empty = {
  package: "",
  preferred_date: "",
  preferred_time: "",
  alt_date: "",
  budget: "",
  project_brief: "",
  name: "",
  email: "",
  phone: "",
  social_links: "",
};

const BookingModal = ({ open, onClose, preselectPackage }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [bookingId, setBookingId] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]); // [{date, reason}]
  const [dateError, setDateError] = useState("");
  const [altDateError, setAltDateError] = useState("");
  const [conflict, setConflict] = useState(null);       // {has_conflict, reason, alternates}
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // hidden — bots often fill anything named "website"
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositPaid, setDepositPaid] = useState(false);
  const openedAtRef = useRef(0);
  // Optional reference-media uploader on the Contact step. Signed one-shot
  // token issued once the email field is valid; no auth needed since the
  // upload is scoped to that email's booking bucket.
  const [uploadToken, setUploadToken] = useState(null);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const emailForTokenRef = useRef("");

  const todayISO = new Date().toISOString().slice(0, 10);
  const blockedSet = new Set(blockedDates.map((b) => b.date));
  const blockedReasonMap = Object.fromEntries(
    blockedDates.map((b) => [b.date, b.reason]),
  );

  useEffect(() => {
    if (open) {
      setStep(preselectPackage ? 1 : 0);
      setForm({ ...empty, package: preselectPackage || "" });
      setError("");
      setDateError("");
      setAltDateError("");
      setBookingId(null);
      setHoneypot("");
      setUploadToken(null);
      setUploadedMedia([]);
      emailForTokenRef.current = "";
      openedAtRef.current = Date.now();
      // Fetch blocked dates once per open
      axios
        .get(`${API}/calendar/blocked-dates`)
        .then(({ data }) => setBlockedDates(data?.blocked || []))
        .catch(() => setBlockedDates([]));
    }
  }, [open, preselectPackage]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Fetch a signed upload token when the user reaches the Contact step
  // with a valid email — enables the optional reference-media uploader.
  useEffect(() => {
    if (!open || step !== 3) return;
    const em = (form.email || "").trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return;
    if (emailForTokenRef.current === em && uploadToken) return;
    emailForTokenRef.current = em;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/media/public/token`, {
          owner_type: "booking",
          owner_id: em,
        });
        setUploadToken(data.token);
      } catch {
        // Non-blocking — form still submits without media
      }
    })();
  }, [open, step, form.email, uploadToken]);

  // Debounced conflict check — fires 400 ms after date/time/package settles.
  useEffect(() => {
    if (!open || !form.preferred_date || blockedSet.has(form.preferred_date)) {
      setConflict(null);
      return undefined;
    }
    const controller = new AbortController();
    setCheckingConflict(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API}/booking/check-conflict`,
          {
            date: form.preferred_date,
            time: form.preferred_time || null,
            package: form.package || null,
          },
          { signal: controller.signal },
        );
        setConflict(data);
      } catch {
        setConflict(null);
      } finally {
        setCheckingConflict(false);
      }
    }, 400);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [open, form.preferred_date, form.preferred_time, form.package, blockedSet]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canAdvance = () => {
    if (step === 0) return !!form.package;
    if (step === 1)
      return (
        !!form.preferred_date &&
        !blockedSet.has(form.preferred_date) &&
        (!form.alt_date || !blockedSet.has(form.alt_date))
      );
    if (step === 2) return form.project_brief.trim().length >= 10;
    if (step === 3)
      return (
        form.name.trim().length > 1 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
      );
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        social_links: form.social_links.trim(),
        package: form.package,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time || "",
        alt_date: form.alt_date || "",
        budget: form.budget ? Number(form.budget) : 0,
        project_brief: form.project_brief.trim(),
        media_ids: uploadedMedia.map((m) => m.id),
        website: honeypot,
        form_ms: openedAtRef.current
          ? Date.now() - openedAtRef.current
          : undefined,
      };
      const { data } = await axios.post(`${API}/bookings`, payload, {
        timeout: 20000,
      });
      setBookingId(data.id);
      setStep(4);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Submission failed";
      setError(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="booking-modal"
        data-testid="booking-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-black/80 backdrop-blur-md"
      >
        <div
          className="min-h-full flex items-start justify-center px-3 py-6 sm:py-10"
          data-testid="booking-modal-backdrop"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl my-4 rounded-3xl bg-[#0a0612] border border-white/10 shadow-[0_30px_120px_rgba(0,240,255,0.18)]"
          >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-7 pt-7 pb-5 bg-[#0a0612] rounded-t-3xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-2">
                [ Book a session ]
              </div>
              <h3 className="font-display font-black text-2xl tracking-tight">
                Tell us about your project
              </h3>
              <div className="text-[#a395c3] text-sm mt-1">
                Your request goes to our booking concierge for vetting — most
                slots confirmed within 24h.
              </div>
            </div>
            <ShareStrip campaign="booking_session" title="Book studio time — SB Media Studio" text="Podcast + video studio in Lakeland, FL. Book your session." href="/#packages" className="mr-1" />
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="booking-modal-close"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {/* Progress */}
          {step < 4 && (
            <div className="px-7 pb-3">
              
            </div>
          )}
          {step < 4 && (
            <div className="px-7 pb-4">
              <div className="flex items-center gap-2">
                {STEPS.slice(0, 4).map((s, i) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= step ? "bg-[#00f0ff]" : "bg-white/10"
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                Step {step + 1} of 4 · {STEPS[step]}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="px-7 pb-7">
            {step === 0 && (
              <PackageGroups
                selected={form.package}
                onSelect={(key) => update("package", key)}
              />
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="booking-step-schedule">
                <Field
                  icon={Calendar}
                  label="Preferred date"
                  required
                >
                  <input
                    type="date"
                    value={form.preferred_date}
                    min={todayISO}
                    onChange={(e) => {
                      const v = e.target.value;
                      update("preferred_date", v);
                      if (v && blockedSet.has(v)) {
                        setDateError(
                          `Studio is closed on this date — ${blockedReasonMap[v] || "unavailable"}. Please pick another.`,
                        );
                      } else {
                        setDateError("");
                      }
                    }}
                    data-testid="booking-preferred-date"
                    className="w-full bg-transparent text-white outline-none text-sm [color-scheme:dark]"
                  />
                </Field>
                {dateError && (
                  <div
                    data-testid="booking-date-error"
                    className="text-[12px] text-[#ff5577] -mt-2"
                  >
                    {dateError}
                  </div>
                )}
                <Field icon={Calendar} label="Preferred time (optional)">
                  <input
                    type="time"
                    value={form.preferred_time}
                    onChange={(e) => update("preferred_time", e.target.value)}
                    data-testid="booking-preferred-time"
                    className="w-full bg-transparent text-white outline-none text-sm [color-scheme:dark]"
                  />
                </Field>

                {/* Conflict / smart-suggestion banner */}
                {conflict && conflict.has_conflict && (
                  <div
                    data-testid="booking-conflict-banner"
                    className="rounded-xl border border-[#f59e0b]/40 bg-[#f59e0b]/10 p-4 text-sm"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={16} className="text-[#f59e0b] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#f59e0b] mb-1">
                          {conflict.kind === "blocked_day" ? "Studio closed that day" : "Slot is taken"}
                        </div>
                        <div className="text-white/80 text-[13px] mb-3">
                          {conflict.reason}
                        </div>
                        {conflict.alternates && conflict.alternates.length > 0 && (
                          <>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2 inline-flex items-center gap-1.5">
                              <Sparkles size={11} className="text-[#00f0ff]" />
                              Sable suggests
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {conflict.alternates.map((a) => (
                                <button
                                  key={`${a.date}-${a.time}`}
                                  type="button"
                                  onClick={() => {
                                    update("preferred_date", a.date);
                                    update("preferred_time", a.time);
                                  }}
                                  data-testid={`conflict-alt-${a.date}-${a.time}`}
                                  className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs font-medium hover:bg-[#00f0ff]/15 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] transition-colors"
                                >
                                  {a.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {conflict && !conflict.has_conflict && form.preferred_date && !checkingConflict && (
                  <div
                    data-testid="booking-conflict-clear"
                    className="text-[12px] text-[#22c55e] inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={12} /> That slot is open.
                  </div>
                )}
                <Field icon={Calendar} label="Backup date (optional)">
                  <input
                    type="date"
                    value={form.alt_date}
                    min={todayISO}
                    onChange={(e) => {
                      const v = e.target.value;
                      update("alt_date", v);
                      if (v && blockedSet.has(v)) {
                        setAltDateError(
                          `Studio is closed on this date — ${blockedReasonMap[v] || "unavailable"}. Please pick another.`,
                        );
                      } else {
                        setAltDateError("");
                      }
                    }}
                    data-testid="booking-alt-date"
                    className="w-full bg-transparent text-white outline-none text-sm [color-scheme:dark]"
                  />
                </Field>
                {altDateError && (
                  <div
                    data-testid="booking-alt-date-error"
                    className="text-[12px] text-[#ff5577] -mt-2"
                  >
                    {altDateError}
                  </div>
                )}
                {blockedDates.length > 0 && (
                  <div
                    data-testid="booking-blocked-hint"
                    className="text-[11px] text-[#a395c3]/70 leading-relaxed pt-1"
                  >
                    <span className="uppercase tracking-[0.24em] text-[#a395c3]/80">
                      Upcoming studio closures:
                    </span>{" "}
                    {blockedDates
                      .filter((b) => b.date >= todayISO)
                      .slice(0, 4)
                      .map((b) => `${b.date} (${b.reason})`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="booking-step-project">
                <Field icon={Mic} label="Project brief" required>
                  <textarea
                    rows={5}
                    value={form.project_brief}
                    onChange={(e) => update("project_brief", e.target.value)}
                    placeholder="What are you making, who's it for, and what does success look like?"
                    data-testid="booking-project-brief"
                    className="w-full bg-transparent text-white outline-none text-sm resize-none placeholder:text-white/30"
                  />
                </Field>
                <Field icon={DollarSign} label="Budget (USD, optional)">
                  <input
                    type="number"
                    min="0"
                    value={form.budget}
                    onChange={(e) => update("budget", e.target.value)}
                    placeholder="e.g. 950"
                    data-testid="booking-budget"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="booking-step-contact">
                <Field icon={User} label="Your name" required>
                  <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Creator"
                    data-testid="booking-name"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Mail} label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@brand.com"
                    data-testid="booking-email"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Phone} label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 813 555 1212"
                    data-testid="booking-phone"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={LinkIcon} label="Social / website (optional)">
                  <input
                    value={form.social_links}
                    onChange={(e) => update("social_links", e.target.value)}
                    placeholder="instagram.com/you · yoursite.com"
                    data-testid="booking-socials"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>

                {/* Optional reference media — mood boards, shot lists, PDF briefs.
                    Unlocks once the email above is valid so we can scope the
                    signed upload token to that booking. */}
                {uploadToken && (
                  <div
                    className="rounded-2xl bg-white/[0.02] border border-white/10 p-4"
                    data-testid="booking-media"
                  >
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                      Optional · Reference media
                    </div>
                    <div className="text-[11px] text-white/60 mb-3 leading-relaxed">
                      Send us mood boards, shot lists, or a PDF brief so we
                      can hit the ground running on your session day.
                    </div>
                    <MediaUploader
                      ownerType="booking"
                      ownerId={form.email.trim().toLowerCase()}
                      authMode="public"
                      publicToken={uploadToken}
                      accept="image/*,application/pdf"
                      maxFiles={10}
                      compact
                      title=""
                      emptyLabel=""
                      testIdPrefix="booking-media"
                      onChange={(next) => setUploadedMedia(next)}
                    />
                  </div>
                )}

                {error && (
                  <div
                    data-testid="booking-error"
                    className="text-xs text-[#ff5577] bg-[#ff0055]/10 border border-[#ff0055]/30 rounded-xl px-3 py-2"
                  >
                    {error}
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div
                className="text-center py-8"
                data-testid="booking-step-done"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 flex items-center justify-center mb-5">
                  <CheckCircle2 size={28} className="text-[#22c55e]" />
                </div>
                <h4 className="font-display font-black text-2xl tracking-tight">
                  Request received.
                </h4>
                <p className="text-[#a395c3] text-sm mt-2 max-w-md mx-auto">
                  Our booking concierge is reviewing it now. You&apos;ll hear back at{" "}
                  <span className="text-white">{form.email}</span> usually
                  within 24 hours. We&apos;ll confirm the slot or come back with a
                  quick clarifying question.
                </p>
                {bookingId && (
                  <>
                    <div
                      className="mt-5 inline-block px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-[#a395c3] font-mono"
                      data-testid="booking-reference-id"
                    >
                      Ref · {bookingId.slice(0, 8).toUpperCase()}
                    </div>
                    {/* Deposit CTA — one-time packages only (custom + subs skip) */}
                    {DEPOSITABLE_PACKAGES.has(form.package) && !depositPaid && (
                      <div className="mt-6 mx-auto max-w-sm rounded-2xl border border-[#00f0ff]/30 bg-[#00f0ff]/[0.04] p-4 text-left">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#00f0ff] mb-2">
                          <Lock size={11} /> Lock your slot
                        </div>
                        <p className="text-white/80 text-sm leading-snug">
                          Secure your studio time with a small deposit now — refundable up to 48 hours before the shoot.
                        </p>
                        <button
                          type="button"
                          onClick={() => setDepositOpen(true)}
                          data-testid="booking-deposit-btn"
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors"
                        >
                          Pay deposit securely
                        </button>
                      </div>
                    )}
                    {depositPaid && (
                      <div className="mt-6 mx-auto max-w-sm rounded-2xl border border-[#22c55e]/30 bg-[#22c55e]/[0.08] p-4">
                        <div className="flex items-center gap-2 text-[#22c55e] font-semibold text-sm">
                          <CheckCircle2 size={16} /> Deposit received · slot is locked
                        </div>
                      </div>
                    )}
                    <div className="mt-5">
                      <a
                        href={`/booking/${bookingId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="booking-track-link"
                        className="inline-flex items-center gap-2 text-sm text-[#00f0ff] hover:text-white transition-colors"
                      >
                        Track your request →
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer nav */}
          {step < 4 && (
            <div className="flex items-center justify-between gap-3 px-7 py-5 bg-[#0a0612] border-t border-white/5 rounded-b-3xl">
              {/* Honeypot — bots often fill anything named "website" */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                aria-hidden="true"
                data-testid="booking-hp-website"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
              />
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="booking-back"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Back
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canAdvance()}
                  data-testid="booking-next"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!canAdvance() || submitting}
                  data-testid="booking-submit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send request <ChevronRight size={14} />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="flex items-center justify-end gap-3 px-7 py-5 bg-[#0a0612] border-t border-white/5 rounded-b-3xl">
              <button
                onClick={onClose}
                data-testid="booking-done-close"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-[#05050a] font-semibold text-sm hover:bg-[#00f0ff] transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </motion.div>
        </div>
      </motion.div>
      {depositOpen && (
        <StripePaymentModal
          open={depositOpen}
          onClose={() => setDepositOpen(false)}
          onSuccess={() => {
            setDepositOpen(false);
            setDepositPaid(true);
          }}
          intent={{
            endpoint: "/payments/package/intent",
            body: {
              package_key: form.package,
              email: form.email,
              name: form.name,
              mode: "deposit",
              booking_id: bookingId,
            },
          }}
          title={`Deposit · ${PACKAGES.find((p) => p.key === form.package)?.label || "Studio time"}`}
          subtitle="Refundable up to 48 hours before your shoot."
        />
      )}
    </AnimatePresence>
  );
};

const Field = ({ icon: Icon, label, required, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-1.5 flex items-center gap-2">
      <Icon size={11} />
      {label}
      {required && <span className="text-[#ff0055]">·</span>}
    </div>
    <div className="rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 focus-within:border-[#00f0ff]/60 transition-colors">
      {children}
    </div>
  </div>
);

const PackageGroups = ({ selected, onSelect }) => {
  const [open, setOpen] = useState(() =>
    Object.fromEntries(PACKAGE_GROUPS.map((g) => [g.key, g.defaultOpen])),
  );
  return (
    <div className="space-y-3" data-testid="booking-step-package">
      {PACKAGE_GROUPS.map((g) => {
        const isOpen = open[g.key];
        return (
          <div
            key={g.key}
            data-testid={`booking-group-${g.key}`}
            className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]"
          >
            <button
              type="button"
              onClick={() => setOpen((p) => ({ ...p, [g.key]: !p[g.key] }))}
              data-testid={`booking-group-toggle-${g.key}`}
              className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/[0.03] transition-colors"
            >
              <div className="text-left">
                <div className="font-display font-bold text-sm">{g.label}</div>
                <div className="text-[11px] text-[#a395c3] mt-0.5">{g.desc}</div>
              </div>
              <ChevronDown
                size={16}
                className={`text-white/50 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen && (
              <div className="px-3 pb-3 space-y-2">
                {g.items.map((p) => {
                  const sel = selected === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => onSelect(p.key)}
                      data-testid={`booking-package-${p.key}`}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all ${
                        sel
                          ? "bg-[#00f0ff]/10 border-[#00f0ff] shadow-[0_0_22px_rgba(0,240,255,0.22)]"
                          : "bg-white/[0.025] border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-display font-bold text-sm">
                          {p.label}
                        </div>
                        <div
                          className={`font-display font-black text-[13px] ${
                            sel ? "text-[#00f0ff]" : "text-white/80"
                          }`}
                        >
                          {p.price}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#a395c3] mt-1 leading-snug">
                        {p.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BookingModal;
